import type { AuthedRequester, AuthedUploader } from "@/lib/brokerAuth";
import { formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S421 — the bond forms Signal presets for a won bid, the stepper's
// "Confirm bond forms" step. Per section (Performance & Payment, Supply,
// Other) PMS lists the forms preset for this bond; the broker confirms each,
// and uploads the obligee's own form where one is missing, choosing its type.
//
// ALL THREE ROUTES ARE PMS-S848's, pinned at BRK-S511 — their answers are
// seam surfaces, and `test/lib/bondFormsContract.test.ts` holds the stub to
// them.
// Nothing is optimistic (NN #7): a confirm or an upload shows only once its
// 2xx answers the refreshed list, which then replaces the one on screen.

const SUBMISSIONS = "/api/broker/bond/submissions";

/** The three boxes, in the wireframe's order. CLOSED: a section this build
 *  has not adopted reads under Other, never dropped. PMS-S848
 *  (P13) — `section`. */
export const BOND_FORM_SECTIONS = [
  "performance_payment",
  "supply",
  "other",
] as const;
export type BondFormSection = (typeof BOND_FORM_SECTIONS)[number];

export const BOND_FORM_SECTION_LABELS: Record<BondFormSection, string> = {
  performance_payment: "Performance & Payment",
  supply: "Supply",
  other: "Other",
};

/** Where a form stands. CLOSED: an unknown status offers nothing.
 *  PMS-S848 (P13) — `status`. */
export const BOND_FORM_STATUSES = ["preset", "confirmed", "uploaded"] as const;
export type BondFormStatus = (typeof BOND_FORM_STATUSES)[number];

/** One form as PMS serves it. */
export interface BondFormWire {
  /** PMS-S848 (P13) — `id`. */
  id: string;
  /** PMS-S848 (P13) — `section`. */
  section: BondFormSection;
  /** PMS-S848 (P13) — `kind`, one of `kinds[].value`. */
  kind: string;
  /** PMS-S848 (P13) — `title`, the form's name. */
  title: string;
  /** PMS-S848 (P13) — `status`. */
  status: BondFormStatus;
  /** PMS-S848 (P13) — `confirmedAt`, ISO, or null. */
  confirmedAt: string | null;
  /** PMS-S848 (P13) — `filename` of an uploaded form, or null. */
  filename: string | null;
}

/** One form type a broker may upload, per section — a SERVED value set, so
 *  the upload's type is a select of PMS's options (D-10), never free text.
 *  PMS-S848 (P13) — `kinds[]`. */
export interface BondFormKindWire {
  value: string;
  label: string;
  section: BondFormSection;
}

/** GET …/bond-forms, and the answer to a confirm or an upload. */
export interface BondFormsWire {
  forms: BondFormWire[];
  kinds: BondFormKindWire[];
}

/** A form as the step renders it: an unknown status is null. */
export interface BondForm extends Omit<BondFormWire, "status"> {
  status: BondFormStatus | null;
}

export interface BondForms {
  forms: BondForm[];
  kinds: BondFormKindWire[];
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const filled = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== "";

const sectionOf = (v: unknown): BondFormSection =>
  BOND_FORM_SECTIONS.find((s) => s === v) ?? "other";

/**
 * The list, tolerantly. A row without an id or a title is dropped; a section
 * this build does not know reads as Other and a status as null. A body that
 * is not the shape is NULL — an unreadable list is not an empty one.
 */
export function readBondForms(raw: unknown): BondForms | null {
  if (!isRecord(raw) || !Array.isArray(raw.forms)) return null;
  const forms: BondForm[] = [];
  for (const f of raw.forms as unknown[]) {
    if (!isRecord(f) || !filled(f.id) || !filled(f.title)) continue;
    forms.push({
      id: f.id,
      section: sectionOf(f.section),
      kind: typeof f.kind === "string" ? f.kind : "",
      title: f.title,
      status: BOND_FORM_STATUSES.find((s) => s === f.status) ?? null,
      confirmedAt: filled(f.confirmedAt) ? f.confirmedAt : null,
      filename: filled(f.filename) ? f.filename : null,
    });
  }
  const kinds: BondFormKindWire[] = [];
  for (const k of Array.isArray(raw.kinds) ? (raw.kinds as unknown[]) : []) {
    if (!isRecord(k) || !filled(k.value) || !filled(k.label)) continue;
    kinds.push({
      value: k.value,
      label: k.label,
      section: sectionOf(k.section),
    });
  }
  return { forms, kinds };
}

/** The forms in each of the three boxes, in PMS's order. */
export function formsBySection(
  forms: readonly BondForm[],
): Record<BondFormSection, BondForm[]> {
  const out: Record<BondFormSection, BondForm[]> = {
    performance_payment: [],
    supply: [],
    other: [],
  };
  for (const f of forms) out[f.section].push(f);
  return out;
}

/** A section's upload types, as select options. */
export function kindsForSection(
  kinds: readonly BondFormKindWire[],
  section: BondFormSection,
): { value: string; label: string }[] {
  return kinds
    .filter((k) => k.section === section)
    .map(({ value, label }) => ({ value, label }));
}

/** The line under a form's title. Dates m/d/yyyy (D-10). */
export function bondFormStatusLine(form: BondForm): string {
  switch (form.status) {
    case "preset":
      return "Preset for this bond";
    case "confirmed":
      return form.confirmedAt
        ? `Confirmed ${formatBondDate(form.confirmedAt)}`
        : "Confirmed";
    case "uploaded":
      return form.filename ? `Uploaded · ${form.filename}` : "Uploaded";
    default:
      return "Status unavailable";
  }
}

async function readOrThrow(body: Promise<unknown>): Promise<BondForms> {
  const read = readBondForms(await body);
  if (read === null) throw new Error("Unreadable bond forms.");
  return read;
}

const formsPath = (submissionId: string) =>
  `${SUBMISSIONS}/${encodeURIComponent(submissionId)}/bond-forms`;

/** GET /api/broker/bond/submissions/{id}/bond-forms (PMS-S848). Throws on a body it cannot read, so the step says so. */
export function getBondForms(
  request: AuthedRequester,
  submissionId: string,
): Promise<BondForms> {
  return readOrThrow(request<unknown>(formsPath(submissionId)));
}

/** POST …/bond-forms/{formId}/confirm — PMS-S848. Answers the
 *  refreshed list. */
export function confirmBondForm(
  request: AuthedRequester,
  submissionId: string,
  formId: string,
): Promise<BondForms> {
  return readOrThrow(
    request<unknown>(
      `${formsPath(submissionId)}/${encodeURIComponent(formId)}/confirm`,
      { method: "POST" },
    ),
  );
}

export interface BondFormUpload {
  file: File;
  section: BondFormSection;
  /** One of the section's served `kinds[].value`. */
  kind: string;
}

/** POST …/bond-forms — the obligee's own form, multipart `{file, section,
 *  kind}`. PMS-S848. Answers the refreshed list. */
export function uploadBondForm(
  upload: AuthedUploader,
  submissionId: string,
  input: BondFormUpload,
): Promise<BondForms> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("section", input.section);
  form.append("kind", input.kind);
  return readOrThrow(upload<unknown>(formsPath(submissionId), form));
}
